import type { ButtonProps } from '@/components/base/button'
import { Button } from '@/components/base/button'
import { Input } from '@/components/base/input'
import type { IconTagName } from '@/components/common/icons/Icon'
import { Icon } from '@/components/common/icons/Icon'
import { FocusManager } from '@/components/FocusManager'
import { RoomLink } from '@/components/RoomLink'
import type { RedirectPath } from '@/helpers/types'
import { cn } from '@/helpers/utils'
import { Label } from '@radix-ui/react-label'
import type { ReactNode } from 'react'
import React, { useId, useRef } from 'react'

const LONG_PRESS_THRESHOLD_MS = 500

const Asterisk = () => (
  <>
    <span className="sr-only">*</span>
    <Icon aria-label="*" tag="asterisk" />
  </>
)

interface DialPadRecord {
  key: string
  label: ReactNode
  subText: string
}

// update the row dimensions to match the dialpad layout
const rowDimensions = [3, 3, 3, 3, 3]

const DialpadDigitsMap = [
  { key: '1', label: '1', subText: '' },
  { key: '2', label: '2', subText: 'A B C' },
  { key: '3', label: '3', subText: 'D E F' },
  { key: '4', label: '4', subText: 'G H I' },
  { key: '5', label: '5', subText: 'J K L' },
  { key: '6', label: '6', subText: 'M N O' },
  { key: '7', label: '7', subText: 'P Q R S' },
  { key: '8', label: '8', subText: 'T U V' },
  { key: '9', label: '9', subText: 'W X Y Z' },
  {
    key: '*',
    label: <Asterisk />,
    subText: '',
  },
  { key: '0', label: '0', subText: '+' },
  { key: '#', label: '#', subText: '' },
] as const satisfies DialPadRecord[]

interface DialpadProps {
  audioChannel: string
  dialpadValue: string
  onClickDirectory: () => void
  onDialpadChange: (value: string) => void
  redirect: RedirectPath
  showDirectoryBtn: boolean
  videoChannel: string
}

const TARGET_ELEMENTS = ['a', 'button'] as const

export const Dialpad = React.forwardRef<HTMLInputElement, DialpadProps>(
  (
    {
      dialpadValue,
      audioChannel,
      videoChannel,
      onClickDirectory,
      onDialpadChange,
      redirect,
      showDirectoryBtn,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const inputId = useId()

    const handleUpdateInput = (value: string) => {
      const newValue = `${dialpadValue}${value}`
      onDialpadChange(newValue)
    }

    const handleClear = () => {
      // clear the input
      onDialpadChange('')
    }

    const handleDelete = () => {
      // delete last character
      const shallowCopy = dialpadValue.slice(0, -1)
      onDialpadChange(shallowCopy)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
      const key = event.key
      const hasControl = event.ctrlKey || event.metaKey

      // alphanumeric and special characters (e.g. @, #, $, %, etc.)
      const validKeys = /^(\w|\W)$/

      const noop = () => {
        // do nothing
      }
      const keyMap = {
        Backspace: hasControl ? handleClear : handleDelete,
        Delete: hasControl ? handleClear : handleDelete,
        Enter: noop,
        Escape: noop,
      }

      const keyAction = keyMap[key as keyof typeof keyMap]
      if (keyAction) {
        keyAction()
      } else {
        if (validKeys.test(key)) {
          handleUpdateInput(key)
        }
      }
    }

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node
      // track the forwarded ref
      if (ref) {
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ref.current = node
        }
      }
    }

    const handleLongPress = (subText: string) => {
      return subText === '+' ? () => handleUpdateInput(subText) : undefined
    }

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      // if touch is used and the target is not the input
      if (
        event.pointerType === 'touch' &&
        inputRef.current &&
        // is not the input element
        event.target !== inputRef.current
      ) {
        // event.preventDefault()
        // FIXME: show the browser/os default context menu for paste? Not sure if this is possible and might require a custom menu
      }
    }

    const handleDialpadChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      onDialpadChange(event.target.value)
    }

    return (
      <div
        aria-label="Dialpad"
        className="flex h-full w-full flex-col items-center justify-center gap-y-4 px-4 py-4"
        role="region"
      >
        <div
          className="flex h-full max-h-40 w-full items-end justify-center"
          onPointerDown={handlePointerDown}
        >
          <Label htmlFor={inputId}>
            <span className="sr-only">Dialpad input</span>
          </Label>
          <Input
            id={inputId}
            //   style like the background so it doesn't look like an input
            className="w-full max-w-prose rounded-none border border-transparent bg-transparent px-4 text-center text-3xl text-background focus-visible:ring-0"
            onChange={handleDialpadChange}
            // TODO: add a visible placeholder?
            value={dialpadValue}
            ref={setRefs}
            type="text"
          />
        </div>
        {/* TODO: only show directory button based on viewport width */}
        <div className="flex h-full w-full flex-col items-center justify-start gap-y-4">
          {!showDirectoryBtn && (
            <Button
              className="flex h-auto flex-col items-center justify-center gap-y-2 px-2 pb-4 pt-3 text-background"
              variant="icon"
              onClick={onClickDirectory}
            >
              <Icon tag="contacts" size="xl" />
              <span className="text-xs font-semibold tracking-[0.5px]">
                Directory
              </span>
            </Button>
          )}
          <FocusManager
            initialFocusIndex={4} // 5 key
            rows={rowDimensions}
            targetElements={TARGET_ELEMENTS}
          >
            <ul
              className="flex w-full flex-col items-center"
              onKeyDown={handleKeyDown}
            >
              <li>
                <ul className="mb-8 grid grid-cols-3 gap-x-7 gap-y-4">
                  {/* FIXME: split into 3 rows each with parent element with role="row" */}
                  {Object.values(DialpadDigitsMap).map(
                    ({ key, label, subText }) => (
                      <li key={key} role="gridcell">
                        <DialPadButton
                          label={label}
                          subText={subText}
                          onClick={() => handleUpdateInput(key)}
                          onLongPress={handleLongPress(subText)}
                        />
                      </li>
                    ),
                  )}
                </ul>
              </li>
              <li role="row">
                <ul aria-label="Dialpad actions" className="mb-4 flex gap-x-7">
                  <li role="gridcell">
                    <DialpadActionLink
                      address={audioChannel}
                      disabled={!audioChannel}
                      redirect={redirect}
                      tag={!audioChannel ? 'phone-disabled' : 'phone-callback'}
                    >
                      Audio
                      <span className="sr-only">Call</span>
                    </DialpadActionLink>
                  </li>
                  <li role="gridcell">
                    <DialpadActionLink
                      address={videoChannel}
                      disabled={!videoChannel}
                      redirect={redirect}
                      tag={!videoChannel ? 'videocam-off' : 'videocam'}
                    >
                      Video
                      <span className="sr-only">Call</span>
                    </DialpadActionLink>
                  </li>
                  {/* TODO: placeholder for delete */}
                  <li role="gridcell">
                    <DialpadActionButton
                      className="border-2 border-muted bg-primary opacity-100 hover:bg-primary hover:opacity-90"
                      onClick={handleDelete}
                    >
                      Delete
                    </DialpadActionButton>
                  </li>
                  {/* TODO: placeholder for clear */}
                  {/* <li>
                    <DialpadActionButton
                      className="border-2 border-muted bg-primary opacity-100 hover:bg-primary hover:opacity-90"
                      onClick={handleClear}
                    >
                      Clear
                    </DialpadActionButton>
                  </li> */}
                </ul>
              </li>
            </ul>
          </FocusManager>
        </div>
      </div>
    )
  },
)
Dialpad.displayName = Dialpad.name

interface DialPadButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: ReactNode
  onLongPress?: (() => void) | undefined
  subText?: string
}

const DialPadButton = ({
  label,
  onClick,
  onLongPress,
  subText,
  ...props
}: DialPadButtonProps) => {
  // button with rounded corners and optional t-9 subtext
  const isLongPressRef = useRef(false)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // check if long press is active
    if (isLongPressRef.current) {
      isLongPressRef.current = false
      return
    }
    // call the onClick function
    onClick?.(event)
  }

  const handleLongPress = (event: React.PointerEvent<HTMLButtonElement>) => {
    let longPressTimeout: NodeJS.Timeout

    if (onLongPress) {
      longPressTimeout = setTimeout(() => {
        isLongPressRef.current = true
      }, LONG_PRESS_THRESHOLD_MS)
    }

    const currentTarget = event.currentTarget

    const handlePointerUp = () => {
      if (isLongPressRef.current) {
        // if long press is active, call the onLongPress function
        onLongPress?.()
      }
      clearTimeout(longPressTimeout)
    }
    currentTarget.addEventListener('pointerup', handlePointerUp, {
      once: true,
    })
    currentTarget.addEventListener('pointercancel', handlePointerUp, {
      once: true,
    })
  }

  return (
    <Button
      className="flex flex-col focus:ring-2 focus:ring-ring focus:ring-offset-2 data-[active='true']:ring-2 data-[active='true']:ring-ring data-[active='true']:ring-offset-2"
      onClick={handleClick}
      onPointerDown={onLongPress ? handleLongPress : undefined}
      onKeyDown={event => event.key === ' ' && event.preventDefault()}
      variant="rounded"
      {...props}
    >
      <span className="text-4xl font-semibold">{label}</span>
      {subText && (
        <span className="font-montserrat text-xs font-normal leading-[4px]">
          {subText}
        </span>
      )}
    </Button>
  )
}

interface DialpadActionButtonProps extends ButtonProps {
  tag?: IconTagName
}

const DialpadActionButton = ({
  children,
  tag,
  ...props
}: DialpadActionButtonProps) => {
  return (
    <DialpadActionButtonBase {...props}>
      {tag && <Icon tag={tag} size="xl" />}
      <span className="text-xs tracking-[0.03rem]">{children}</span>
    </DialpadActionButtonBase>
  )
}
interface DialpadActionLinkProps extends ButtonProps {
  address: string
  redirect: RedirectPath
  tag?: IconTagName
}

const DialpadActionLink = ({
  address,
  children,
  redirect,
  tag,
  ...props
}: DialpadActionLinkProps) => {
  return (
    <DialpadActionButtonBase
      asChild
      className="aria-disabled:opacity-50"
      {...props}
    >
      <RoomLink
        address={address}
        disabled={props.disabled || false}
        redirect={redirect}
      >
        {tag && <Icon tag={tag} size="xl" />}
        <span className="text-xs tracking-[0.03rem]">{children}</span>
      </RoomLink>
    </DialpadActionButtonBase>
  )
}

type DialpadActionBaseProps = ButtonProps

const DialpadActionButtonBase = ({
  children,
  className,
  ...props
}: DialpadActionBaseProps) => {
  return (
    <Button
      className={cn(
        'flex flex-col gap-y-1 bg-sw-green hover:bg-sw-green/90 focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className,
      )}
      onKeyDown={event => event.key === ' ' && event.preventDefault()}
      variant="rounded"
      {...props}
    >
      {children}
    </Button>
  )
}
